/**
 * 画像URL管理ユーティリティ
 *
 * @file image.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 画像URLの生成・取得を統一管理する。
 * 環境変数ベースURLとの結合、プレースホルダー処理を提供。
 */

/**
 * 環境変数から画像ベースURLを取得
 */
const getImageBaseUrl = (): string => {
  return import.meta.env.VITE_IMAGES_BASE_URL || '';
};

/**
 * デフォルトプレースホルダー画像パス
 */
const DEFAULT_PLACEHOLDER = '/ga/placeholder.jpg';

/**
 * 単一画像URLを取得
 * @param imageUrl - 画像URL（相対パスまたは絶対URL）
 * @param placeholder - プレースホルダー画像パス（省略可）
 * @returns 完全な画像URL
 */
export const getImageUrl = (
  imageUrl: string | null | undefined,
  placeholder: string = DEFAULT_PLACEHOLDER
): string => {
  if (!imageUrl) {
    imageUrl = placeholder;
  }

  // 既に絶対URLの場合はそのまま返す
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // /ga/ (商品画像) /ca/ (カテゴリー画像) /ba/ (バナー画像) /rp/ (再梱包写真)
  // /static/ (購入代行依頼フォーム添付画像等、backend/static配下) は環境変数ベースURLと結合
  if (
    imageUrl.startsWith('/ga/') ||
    imageUrl.startsWith('/ca/') ||
    imageUrl.startsWith('/ba/') ||
    imageUrl.startsWith('/rp/') ||
    imageUrl.startsWith('/static/')
  ) {
    const baseUrl = getImageBaseUrl();
    return baseUrl ? `${baseUrl}${imageUrl}` : imageUrl;
  }

  // その他のスラッシュで始まる場合はそのまま返す（静的アセット等）
  if (imageUrl.startsWith('/')) {
    return imageUrl;
  }

  // 上記以外の相対パスも環境変数ベースURLと結合
  const baseUrl = getImageBaseUrl();
  return baseUrl ? `${baseUrl}${imageUrl}` : imageUrl;
};

/**
 * 配列画像の最初のURLを取得
 * @param images - 画像URL配列
 * @param index - 取得するインデックス（省略時は0）
 * @param placeholder - プレースホルダー画像パス（省略可）
 * @returns 完全な画像URL
 */
export const getFirstImageUrl = (
  images: string[] | null | undefined,
  index: number = 0,
  placeholder: string = DEFAULT_PLACEHOLDER
): string => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return getImageUrl(undefined, placeholder);
  }

  const imageUrl = images[index];
  return getImageUrl(imageUrl, placeholder);
};
